import {
  assoc,
  compose,
  find,
  findIndex,
  identity,
  lens,
  Lens,
  lensIndex,
  lensProp,
  propEq,
  update,
} from 'ramda';
import { isObject } from './ramdaCookbook';

/** En lens som pekar på hela datastrukturen. Effekten blir som att inte använda en lens, men användbart när lensen är en parameter eller som basfall */
export const identityLens = lens(identity, identity);

export type LensDef = Lens | string | number;
type ComposeLensesType = (...args: LensDef[]) => Lens;
/**Slår ihop flera Lenses till en.
 * Implementationen är samma som Ramda.compose,
 * men med typning så Typescript förstår att resultatet av att "composa" två Lenses alltid är en Lens */
export const composeLenses: ComposeLensesType =
  compose as unknown as ComposeLensesType;

const lensDefToLens = (def: LensDef): Lens =>
  typeof def === 'string'
    ? lensProp(def)
    : typeof def === 'number'
    ? lensIndex(def)
    : def;

/** Slår ihop flera lenses till en. Istället för en lens kan man ange strängar eller nummer för att bygga en path i objekt och listor */
export const composeLensDefs = (...args: LensDef[]): Lens => {
  const lenses = args.map(lensDefToLens);
  if (lenses.length === 0) {
    return identityLens;
  }
  return composeLenses(...lenses);
};

/**Lens som hittar en property på ett objekt om det finns och annars försöker med en annan property */
export const propsLens = (prop1: string, prop2: string): Lens =>
  lens(
    (obj: any) => {
      return obj?.[prop1] ?? obj?.[prop2];
    },
    (newVal, obj) => {
      if (typeof obj === 'object') {
        if (obj[prop1] == null && obj[prop2] != null) {
          return assoc(prop2, newVal, obj);
        }
        return assoc(prop1, newVal, obj);
      }
      return { [prop1]: newVal };
    }
  );
/**Lens som hittar objekt med ett visst id i en lista */
export const idLens = (id: string): Lens =>
  lens(
    list => {
      if (Array.isArray(list)) {
        return find(propEq('id', id), list.filter(isObject));
      }
      return undefined;
    },
    (newVal, list) => {
      if (Array.isArray(list)) {
        const idx = findIndex(propEq('id', id), list.filter(isObject));
        if (idx > -1) {
          return update(idx, newVal, list);
        } else {
          return list.concat(newVal);
        }
      }
      return [newVal];
    }
  );
